import type { LensDatasetColumn } from '~/types/leLens';

import { isDateLikeText, isMeasureColumn } from './lensChart';

/** Currency and unit suffixes that name the column's units, not its subject. */
export const UNIT_SUFFIX = /_(aud|usd|nzd|gbp|eur|sgd|pct|percent)$/;

/**
 * Measures a reader reaches for first. Column order is the order the query
 * happened to be written in, which is not the order anyone cares about: on a
 * commercial board the money is the headline and the counts explain it.
 */
export const HEADLINE_MEASURE = /(revenue|margin|ttv|sales|value|amount|spend|cost|profit)/i;

/**
 * Ranks measure columns so a money measure leads, keeping every other column
 * in its original relative order. Column-name based only, so it ranks the
 * same way whether the caller found its measures by reading sample values or
 * by reading names alone.
 */
export function rankMeasuresMoneyFirst(measures: ReadonlyArray<string>): Array<string> {
  return [...measures].sort((a, b) => Number(HEADLINE_MEASURE.test(b)) - Number(HEADLINE_MEASURE.test(a)));
}

/**
 * Commercial vocabulary that is written as an acronym rather than a word, keyed
 * lowercase. Blanket title casing would give "Ttv" and "Mroas", which reads as
 * a typo on a dashboard somebody is meant to trust, so the exceptions are
 * listed rather than guessed at.
 */
const ACRONYMS: Record<string, string> = {
  aov: 'AOV',
  ar: 'AR',
  br: 'BR',
  cac: 'CAC',
  gha: 'GHA',
  gm: 'GM',
  lpc: 'LPC',
  lpp: 'LPP',
  ltlp: 'LTLP',
  ltv: 'LTV',
  mroas: 'mROAS',
  nha: 'NHA',
  ota: 'OTA',
  roas: 'ROAS',
  spp: 'SPP',
  ttv: 'TTV',
};

/** A column name as a reader would say it: `revenue_aud` is "revenue". */
export function columnLabel(column: string): string {
  const words = column.replace(UNIT_SUFFIX, '').replace(/[_-]+/g, ' ').trim();
  if (!words) {
    return column;
  }
  return words
    .split(' ')
    .map((word) => ACRONYMS[word.toLowerCase()] ?? word)
    .join(' ');
}

/** A label used to open a card title, so its first word carries a capital
 *  unless it is an acronym that already has its own casing. */
export function sentenceCase(label: string): string {
  const [first, ...rest] = label.split(' ');
  if (!first) {
    return label;
  }
  const opener = ACRONYMS[first.toLowerCase()] ? first : first.charAt(0).toUpperCase() + first.slice(1);
  return [opener, ...rest].join(' ');
}

export type ColumnClassification = {
  measures: Array<string>;
  dated: boolean;
  dimensions: Array<string>;
};

/**
 * A column that identifies a row rather than measuring one. Summing an identifier produces a
 * number that means nothing, so it belongs with the dimensions whatever else its name says:
 * `order_id`, `booking_id` and `room_id` each carry a MEASURE_NAME_HINT word, and the row-based
 * classifier cannot tell them apart from a quantity either, because an id is numeric.
 *
 * Checked before the measure hints in both classifiers, so a proposed card is never a total of
 * an id. The name is the only signal available for this: `isMeasureColumn` reads values, and the
 * values of an id column are indistinguishable from a count.
 */
export const IDENTIFIER_NAME = /(^|_)(id|ids|bk|key|keys|uuid|guid|code|pk|sk)($|_)/i;

/**
 * Classifies a source's columns from its own sample rows: a column is a
 * measure only when its actual values are numeric (isMeasureColumn), and the
 * source counts as dated only when some column's actual values read as a
 * calendar date (isDateLikeText). This is the classification cardStarters
 * has always used, lifted out here so a second call site can share it
 * instead of reimplementing it. cardStarters calls this with the same rows
 * it always had, so its output is unchanged.
 */
export function classifyColumnsFromRows(source: {
  rows: Array<Record<string, unknown>>;
  columns: Array<string>;
}): ColumnClassification {
  const measures = rankMeasuresMoneyFirst(
    source.columns.filter((column) => !IDENTIFIER_NAME.test(column) && isMeasureColumn(source.rows, column)),
  );
  const dated = source.columns.some((column) => source.rows.some((row) => isDateLikeText(row[column])));
  const dimensions = source.columns.filter(
    (column) => !measures.includes(column) && !source.rows.some((row) => isDateLikeText(row[column])),
  );
  return { measures, dated, dimensions };
}

/**
 * A hint, beyond the money words HEADLINE_MEASURE already carries, that a
 * column holds a countable or measurable quantity. Still column-name based:
 * see classifyColumnsByName for why that is all this can go on.
 */
const MEASURE_NAME_HINT =
  /(count|total|sum|number|qty|quantity|rate|score|price|fee|order|booking|visit|session|click|impression|conversion|night|room|guest|lead|view|unit)/i;

/** A column name that reads as a date or a time bucket, judged from the name
 *  alone. */
const DATE_NAME_HINT = /(^|_)(date|day|month|week|year|period|created_at|updated_at)($|_)/i;

export type NameClassification = {
  measures: Array<string>;
  dateColumn: string | undefined;
  dimensions: Array<string>;
};

/**
 * Classifies a dataset's columns from their declared names only, with no
 * sample rows to check.
 *
 * This exists for the one call site that has no rows to look at: the create
 * dialog's dataset-to-cards expander in planGateResolve.ts. A WorkspaceCandidate
 * there carries `columns: Array<string>` and nothing behind them, since
 * choosing a dataset in the create dialog runs no query. Name-based
 * classification is strictly less accurate than classifyColumnsFromRows: a
 * numeric column named `status_code` reads as a dimension here, and a text
 * column named `revenue_note` reads as a measure, because there is no value
 * to check either way. It trades that accuracy for working before any query
 * has run, which is the only way this call site can classify anything
 * without fabricating rows to feed the row-based helper.
 */
export function classifyColumnsByName(columns: ReadonlyArray<string>): NameClassification {
  const dateColumn = columns.find((column) => DATE_NAME_HINT.test(column));
  const measures = rankMeasuresMoneyFirst(
    columns.filter(
      (column) =>
        column !== dateColumn &&
        !IDENTIFIER_NAME.test(column) &&
        (UNIT_SUFFIX.test(column) || HEADLINE_MEASURE.test(column) || MEASURE_NAME_HINT.test(column)),
    ),
  );
  const dimensions = columns.filter((column) => column !== dateColumn && !measures.includes(column));
  return { measures, dateColumn, dimensions };
}

export type DeclaredColumnClassification = {
  measures: Array<string>;
  dateColumn: string | undefined;
  dimensions: Array<string>;
  calculated: Array<string>;
};

/**
 * Classifies a dataset's columns from their declared kind, populated by a
 * BigQuery dry run, rather than a guess at what a name implies. This is what
 * a dataset that has already declared its columns is classified by; a column
 * can only be mistaken here if it was declared wrong, not because a word like
 * "order" or "room" happened to appear in its name.
 *
 * `classifyColumnsByName` stays as the fallback for a dataset that has not
 * run a dry run yet and so carries no `kind` for any column: a "not yet
 * declared" dataset still has to offer something to build a card from.
 */
export function classifyDeclaredColumns(columns: ReadonlyArray<LensDatasetColumn>): DeclaredColumnClassification {
  const measures = rankMeasuresMoneyFirst(columns.filter((column) => column.kind === 'measure').map((c) => c.name));
  const dateColumn = columns.find((column) => column.kind === 'date')?.name;
  const dimensions = columns.filter((column) => column.kind === 'dimension').map((c) => c.name);
  const calculated = columns.filter((column) => column.kind === 'calculated').map((c) => c.name);
  return { measures, dateColumn, dimensions, calculated };
}

/**
 * A column holding a per-unit number: a rate, a percentage, a ratio or a
 * return. Such a column already carries a denominator, so combining rows of it
 * by adding them produces a number with no meaning, and averaging them weights
 * a row covering ten orders the same as one covering ten thousand.
 *
 * Deliberately narrow. A warning a reader learns to dismiss is worse than no
 * warning, so this matches only words that name a per-unit quantity outright.
 * `margin_aud` and `revenue` are amounts and are not matched; `margin_pct` is.
 */
export const RATIO_NAME = /(^|_)(rate|pct|percent|percentage|ratio|share|roas|mroas|cvr|ctr|yield)($|_)/i;

/**
 * A word that turns a per-unit-sounding column into a label. `rate_plan` and
 * `rate_type` name which rate a booking was sold on, so they group rows rather
 * than measuring them, and reporting a rate hazard against them would be
 * wrong about what the column is.
 */
const LABEL_SUFFIX =
  /_(plan|plans|type|types|name|names|group|groups|class|classes|band|bands|bucket|buckets|tier|tiers|label|labels|category|categories|status)($|_)/i;

/**
 * A column whose values are already an average or a middle. Adding them gives
 * a number that is not an average of anything, and averaging them again gives
 * an average of averages, which is not the overall average unless every group
 * happens to be the same size.
 */
export const PRE_AVERAGED_NAME =
  /(^|_)(avg|average|mean|median|aov|arpu|arpo|per_order|per_booking|per_night|per_customer|per_room|per_guest)($|_)/i;

/** How a combine choice can go wrong, and the one way out of it. */
export type AggregateHazard = {
  /** What the resulting number would actually be. Plain business English. */
  message: string;
  /** How bad: `wrong` is never right, `weighted` can be right if intended. */
  severity: 'wrong' | 'weighted';
  /** A combine choice that is right for this column, when one exists. */
  suggest?: { aggregate: 'avg' | 'countDistinct' | 'max'; label: string };
};

/**
 * Whether combining a column this way produces a number a reader should not
 * trust, judged from the column name alone.
 *
 * The name is the only signal the card editor has here: choosing a dataset
 * column offers no sample values, and the rows a card already loaded are the
 * pre-change shape rather than the one the reader is about to save. A rate
 * summed is wrong whatever its values look like, so the name is enough for the
 * cases this reports and is the reason it reports nothing outside them.
 */
export function aggregateHazard(column: string, aggregate: string): AggregateHazard | null {
  const trimmed = column.trim();
  if (!trimmed) {
    return null;
  }
  // The column's own name, not its prettified label: the field picker directly
  // above this message lists raw column names, and a message about "product bk"
  // when the control says `product_bk` reads as being about something else.
  const name = trimmed;

  if (IDENTIFIER_NAME.test(trimmed) && ['sum', 'avg', 'min', 'max'].includes(aggregate)) {
    return {
      message: `${name} identifies a row rather than measuring one, so combining it produces a number that means nothing. Count how many distinct values there are instead.`,
      severity: 'wrong',
      suggest: { aggregate: 'countDistinct', label: 'Count distinct values' },
    };
  }

  if (LABEL_SUFFIX.test(trimmed)) {
    return null;
  }
  const isRatio = RATIO_NAME.test(trimmed);
  const isPreAveraged = PRE_AVERAGED_NAME.test(trimmed);

  if (aggregate === 'sum' && isRatio) {
    return {
      message: `${name} is a rate, so adding rows of it together does not give a rate. Two days at 30% do not make 60%.`,
      severity: 'wrong',
      suggest: { aggregate: 'avg', label: 'Average' },
    };
  }
  if (aggregate === 'sum' && isPreAveraged) {
    return {
      message: `${name} is already an average, so adding rows of it together does not give an average of anything.`,
      severity: 'wrong',
      suggest: { aggregate: 'avg', label: 'Average' },
    };
  }
  if (aggregate === 'avg' && (isRatio || isPreAveraged)) {
    return {
      message: `${name} already carries its own denominator, so this average counts every row equally: a day with ten orders moves it as much as a day with ten thousand. The overall figure has to be worked out in the query, from the totals above and below the line.`,
      severity: 'weighted',
    };
  }

  return null;
}
